import { TrophyIcon } from '@bravemove/design-tokens/icons';
import { MatchCard, type MatchDetail } from '../MatchCard/MatchCard';
import { StandingsTable, type StandingsRow } from '../StandingsTable/StandingsTable';
import { EmptyState } from '../EmptyState/EmptyState';
import { cn } from '../../utils/cn';
import { computeBracketLayout } from './bracketLayout';

/**
 * F41 — Bracket view (web) — the flagship in-play component.
 *
 * Renders one division's live bracket from a `BracketView` payload:
 *   - `single_elimination` → a horizontally-scrollable knockout LADDER. Each
 *     round is a column; a round-N match is vertically centred between its two
 *     ACTUAL feeder matches — its centre is the average of its feeders' centres,
 *     derived from the `nextMatchId` wiring (`bracketLayout.ts`). This is robust
 *     to byes: a non-power-of-two field gives byes to top seeds, so a round-2
 *     match may be fed by a bye seed with no round-1 match — tree-derived
 *     centring keeps every match aligned with its feeders where flex
 *     `justify-around` would not. Connectors are drawn per target match (a stub
 *     out of each feeder, a vertical spine to the target's centre, a line into
 *     it); segments on the path the *winner* advanced through are tinted
 *     `accent` so the champion's run is traceable. A distinct champion node
 *     terminates the ladder.
 *   - `round_robin` / `league` → a `StandingsTable` (leader emphasised) plus a
 *     per-round match list.
 *
 * Pre-generation (`bracketGeneratedAt === null` and no stages) shows a
 * "bracket being generated" EmptyState.
 *
 * Layout is absolute positioning over a fixed row pitch + token border
 * utilities (no canvas, no connector-line library) — each match sits at
 * `centre × ROW_PITCH`, and connectors are bordered `<div>`s. Rounds fade in on
 * mount (staggered, gated on `prefers-reduced-motion`).
 *
 * The read shapes below are structurally identical to `BracketView` /
 * `StageDetail` / `RoundDetail` from `@bravemove/types` — mirrored locally (and not
 * imported) to keep `@bravemove/ui-web` dep-free.
 */

export type TournamentDivisionFormat = 'single_elimination' | 'round_robin' | 'league';
export type TournamentStageKind = 'group' | 'knockout' | 'league';

/** Structurally identical to `RoundDetail`. */
export interface RoundDetail {
  readonly id: string;
  readonly ordinal: number;
  readonly name: string | null;
  readonly status: 'pending' | 'active' | 'completed';
  readonly matches: ReadonlyArray<MatchDetail>;
}

/** Structurally identical to `StageDetail`. */
export interface StageDetail {
  readonly id: string;
  readonly kind: TournamentStageKind;
  readonly ordinal: number;
  readonly format: TournamentDivisionFormat;
  readonly status: 'pending' | 'active' | 'completed';
  readonly rounds: ReadonlyArray<RoundDetail>;
}

/** Structurally identical to `BracketView` from `@bravemove/types`. */
export interface BracketViewData {
  readonly divisionId: string;
  readonly tournamentId: string;
  readonly format: TournamentDivisionFormat;
  readonly bracketGeneratedAt: string | null;
  readonly championRegistrationId: string | null;
  readonly stages: ReadonlyArray<StageDetail>;
  readonly standings: ReadonlyArray<StandingsRow>;
}

export interface BracketViewProps {
  readonly bracket: BracketViewData;
  /** Fired when a match node is clicked (organizer score-entry / public detail). */
  readonly onMatchPress?: ((match: MatchDetail) => void) | undefined;
  /** Resolve a `hostId` → display name for match footers. */
  readonly resolveHostName?: ((hostId: string) => string | undefined) | undefined;
  readonly className?: string | undefined;
}

/**
 * One-shot keyframes for the staggered round reveal + the live-spine breathe.
 * Token-free (opacity/transform only — no colours), injected once so the
 * component is self-contained regardless of which app CSS is loaded. All
 * animation is wrapped in `@media (prefers-reduced-motion: no-preference)`.
 */
const BRACKET_STYLE_ID = 'bravemove-bracket-keyframes';
const BRACKET_KEYFRAMES = `
@media (prefers-reduced-motion: no-preference) {
  @keyframes bravemove-bracket-round-in {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: none; }
  }
  .bravemove-bracket-round { opacity: 0; animation: bravemove-bracket-round-in 320ms cubic-bezier(0.4,0,0.2,1) both; }
}
`;

function BracketKeyframes() {
  // Inert <style>; React de-dupes by id across instances on the same page.
  return <style id={BRACKET_STYLE_ID}>{BRACKET_KEYFRAMES}</style>;
}

function isKnockoutStage(stage: StageDetail): boolean {
  return stage.kind === 'knockout' || stage.format === 'single_elimination';
}

interface StageRenderProps {
  readonly stage: StageDetail;
  readonly onMatchPress?: ((match: MatchDetail) => void) | undefined;
  readonly resolveHostName?: ((hostId: string) => string | undefined) | undefined;
  readonly championRegistrationId: string | null;
}

/**
 * The set of winner ids in a stage — used to tint the connector segments the
 * champion advanced through.
 */
function collectWinners(rounds: ReadonlyArray<RoundDetail>): Set<string> {
  const winners = new Set<string>();
  for (const round of rounds) {
    for (const m of round.matches) {
      if (m.winnerRegistrationId) winners.add(m.winnerRegistrationId);
    }
  }
  return winners;
}

/**
 * Vertical geometry of the absolute-positioned ladder. `ROW_PITCH` is the
 * centre-to-centre distance between two adjacent round-1 slots; `ROW_H` is the
 * fixed slot height a match card is vertically centred within.
 */
const ROW_PITCH = 132;
const ROW_H = 112;
const ROUND_W = '15rem';
const CONNECTOR_W = 32;

interface ChampionNodeProps {
  readonly name: string | null;
  readonly index: number;
}

/** The terminal champion reveal at the end of the ladder. */
function ChampionNode({ name, index }: ChampionNodeProps) {
  return (
    <div
      className="bravemove-bracket-round flex shrink-0 flex-col justify-center"
      style={{ animationDelay: `${index * 90}ms`, width: '13rem' }}
    >
      <div className="mb-2 font-mono text-[11px] font-bold uppercase tracking-wider text-tertiary">
        Champion
      </div>
      <div className="flex flex-col items-center gap-2 rounded-xl border-2 border-accent bg-accent-soft px-3 py-5 text-center">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-ink">
          <TrophyIcon size={22} color="currentColor" />
        </span>
        <span className="truncate text-[14px] font-bold text-ink">{name ?? 'Champion'}</span>
        <span className="font-mono text-[9px] uppercase tracking-wider text-accent">Winner</span>
      </div>
    </div>
  );
}

/** Resolve the champion's display name from the decided final match. */
function findChampionName(
  rounds: ReadonlyArray<RoundDetail>,
  championRegistrationId: string,
): string | null {
  for (const round of rounds) {
    for (const m of round.matches) {
      if (m.participantA?.registrationId === championRegistrationId) {
        return m.participantA.displayName;
      }
      if (m.participantB?.registrationId === championRegistrationId) {
        return m.participantB.displayName;
      }
    }
  }
  return null;
}

/** A short uppercase mono round header (Done / Live status to its right). */
function RoundHeader({ round }: { round: RoundDetail }) {
  return (
    <div className="mb-2 flex h-5 items-center justify-between gap-2">
      <span className="truncate font-mono text-[11px] font-bold uppercase tracking-wider text-ink-2">
        {round.name ?? `Round ${round.ordinal}`}
      </span>
      {round.status === 'completed' ? (
        <span className="font-mono text-[9px] uppercase tracking-wider text-success">Done</span>
      ) : round.status === 'active' ? (
        <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wider text-accent">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-accent motion-safe:animate-pulse" />
          Live
        </span>
      ) : null}
    </div>
  );
}

/**
 * A single-elimination knockout ladder. Each match is positioned absolutely at
 * its tree-derived centre (`bracketLayout.ts`) so a round-N match sits exactly
 * between its ACTUAL feeders — correct even when round 1 has byes. Connectors
 * are drawn per target match: a stub out of each feeder, a vertical spine to the
 * target's centre, and a line into the target.
 */
function KnockoutTree({
  stage,
  onMatchPress,
  resolveHostName,
  championRegistrationId,
}: StageRenderProps) {
  const rounds = [...stage.rounds].sort((a, b) => a.ordinal - b.ordinal);
  const winners = collectWinners(rounds);
  const championName =
    championRegistrationId !== null ? findChampionName(rounds, championRegistrationId) : null;

  const layout = computeBracketLayout(
    rounds.map((r) => ({
      id: r.id,
      ordinal: r.ordinal,
      matches: r.matches.map((m) => ({ id: m.id, nextMatchId: m.nextMatchId ?? null })),
    })),
  );

  // Normalise every centre to a 0-based pixel top so the tallest round anchors
  // the column height; `slot` is the centre relative to the topmost match.
  const slotOf = (matchId: string): number => {
    const c = layout.byMatchId.get(matchId)?.center;
    return (c ?? layout.minCenter) - layout.minCenter;
  };
  const topOf = (matchId: string): number => slotOf(matchId) * ROW_PITCH;
  /** The vertical centre (px) of a match within the absolute canvas. */
  const centreOf = (matchId: string): number => topOf(matchId) + ROW_H / 2;

  const spanSlots = layout.maxCenter - layout.minCenter;
  const canvasHeight = spanSlots * ROW_PITCH + ROW_H;

  const advanced = (m: MatchDetail | undefined): boolean =>
    !!m?.winnerRegistrationId && winners.has(m.winnerRegistrationId);

  // The final (last decided) round's single match anchors the champion node.
  const lastRound = rounds[rounds.length - 1];
  const finalMatch = lastRound?.matches[0];
  const championTop = finalMatch ? topOf(finalMatch.id) : 0;

  return (
    <div className="relative">
      <div className="overflow-x-auto pb-2">
        <div className="flex min-w-min items-start">
          {rounds.map((round, i) => {
            const isLastRound = i === rounds.length - 1;
            return (
              <div key={round.id} className="flex items-start">
                {/* Round column — matches positioned absolutely at their centre. */}
                <div
                  className="bravemove-bracket-round shrink-0"
                  style={{ animationDelay: `${i * 90}ms`, width: ROUND_W }}
                >
                  <RoundHeader round={round} />
                  <div className="relative" style={{ height: canvasHeight }}>
                    {round.matches.length === 0 ? (
                      <div
                        className="absolute inset-x-0 flex items-center justify-center rounded-lg border border-dashed border-hairline-2 p-3 text-center text-[11px] text-ink-3"
                        style={{ top: 0, height: ROW_H }}
                      >
                        {i === 0 ? 'No matches yet' : 'Awaiting prior round'}
                      </div>
                    ) : (
                      round.matches.map((match) => (
                        <div
                          key={match.id}
                          className="absolute inset-x-0 flex items-center"
                          style={{ top: topOf(match.id), height: ROW_H }}
                        >
                          <div className="w-full">
                            <MatchCard
                              match={match}
                              onPress={onMatchPress ? () => onMatchPress(match) : undefined}
                              hostName={match.hostId ? resolveHostName?.(match.hostId) : undefined}
                            />
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Connector canvas into the next round (per target match). */}
                {!isLastRound && round.matches.length > 0 ? (
                  <div
                    className="relative shrink-0"
                    style={{ width: CONNECTOR_W, marginTop: '1.75rem', height: canvasHeight }}
                    aria-hidden="true"
                  >
                    {rounds[i + 1]?.matches.map((target) => {
                      const targetCentre = centreOf(target.id);
                      const feederIds = layout.byMatchId.get(target.id)?.feederIds ?? [];
                      const feeders = round.matches.filter((m) => feederIds.includes(m.id));
                      if (feeders.length === 0) return null;
                      const targetAdvanced = feeders.some(advanced);
                      return feeders.map((feeder) => {
                        const fCentre = centreOf(feeder.id);
                        const fAdvanced = advanced(feeder);
                        const hi = Math.min(fCentre, targetCentre);
                        const lo = Math.max(fCentre, targetCentre);
                        return (
                          <div key={`${target.id}:${feeder.id}`}>
                            {/* Stub out of the feeder (left half). */}
                            <span
                              className={cn(
                                'absolute h-px',
                                fAdvanced ? 'bg-accent' : 'bg-hairline-2',
                              )}
                              style={{ top: fCentre, left: 0, width: CONNECTOR_W / 2 }}
                            />
                            {/* Vertical spine from feeder centre to target centre. */}
                            <span
                              className={cn(
                                'absolute w-px',
                                fAdvanced ? 'bg-accent' : 'bg-hairline-2',
                              )}
                              style={{ top: hi, height: lo - hi, left: CONNECTOR_W / 2 }}
                            />
                            {/* Line into the target (right half). */}
                            <span
                              className={cn(
                                'absolute h-px',
                                targetAdvanced ? 'bg-accent' : 'bg-hairline-2',
                              )}
                              style={{
                                top: targetCentre,
                                left: CONNECTOR_W / 2,
                                width: CONNECTOR_W / 2,
                              }}
                            />
                          </div>
                        );
                      });
                    })}
                  </div>
                ) : (
                  <div className="w-6 shrink-0" aria-hidden="true" />
                )}
              </div>
            );
          })}
          {/* Champion node terminates the ladder once crowned — aligned to the
              final match's centre. */}
          {championRegistrationId !== null ? (
            <div className="shrink-0" style={{ marginTop: '1.75rem' }}>
              <div style={{ paddingTop: championTop }}>
                <ChampionNode name={championName} index={rounds.length} />
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {/* Right-edge fade so it reads as horizontally scrollable. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 end-0 w-8 bg-gradient-to-l from-bg to-transparent"
      />
    </div>
  );
}

/** Round-robin / league: a flat match list grouped by round. */
function RoundList({ stage, onMatchPress, resolveHostName }: StageRenderProps) {
  const rounds = [...stage.rounds].sort((a, b) => a.ordinal - b.ordinal);
  return (
    <div className="flex flex-col gap-4">
      {rounds.map((round, i) => (
        <div
          key={round.id}
          className="bravemove-bracket-round"
          style={{ animationDelay: `${i * 60}ms` }}
        >
          <div className="mb-2 flex items-center gap-2">
            <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-ink-2">
              {round.name ?? `Round ${round.ordinal}`}
            </span>
            {round.status === 'completed' ? (
              <span className="font-mono text-[9px] uppercase tracking-wider text-success">
                Done
              </span>
            ) : round.status === 'active' ? (
              <span className="font-mono text-[9px] uppercase tracking-wider text-accent">
                Live
              </span>
            ) : null}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {round.matches.map((match) => (
              <MatchCard
                key={match.id}
                match={match}
                compact
                onPress={onMatchPress ? () => onMatchPress(match) : undefined}
                hostName={match.hostId ? resolveHostName?.(match.hostId) : undefined}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function BracketView({
  bracket,
  onMatchPress,
  resolveHostName,
  className,
}: BracketViewProps) {
  const hasStructure = bracket.stages.length > 0 || bracket.bracketGeneratedAt !== null;

  if (!hasStructure) {
    return (
      <div className={className}>
        <EmptyState
          icon={<TrophyIcon size={32} color="currentColor" />}
          title="Bracket not generated yet"
          description="The bracket will appear here once the organizer generates it from the confirmed roster."
        />
      </div>
    );
  }

  const isLeagueLike = bracket.format === 'round_robin' || bracket.format === 'league';
  const stages = [...bracket.stages].sort((a, b) => a.ordinal - b.ordinal);

  return (
    <div className={cn('flex flex-col gap-5', className)}>
      <BracketKeyframes />

      {bracket.championRegistrationId ? (
        <div className="flex items-center gap-2 rounded-lg border border-accent/40 bg-accent-soft px-3 py-2">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-primary-ink">
            <TrophyIcon size={15} color="currentColor" />
          </span>
          <span className="text-[13px] font-bold text-ink">Champion crowned</span>
        </div>
      ) : null}

      {/* Standings (round-robin / league) — lead with the table. */}
      {isLeagueLike && bracket.standings.length > 0 ? (
        <div>
          <div className="mb-2 text-[12px] font-bold text-ink">Standings</div>
          <StandingsTable rows={bracket.standings} />
        </div>
      ) : null}

      {/* Per-stage structure */}
      {stages.map((stage) => (
        <div key={stage.id}>
          {stages.length > 1 ? (
            <div className="mb-2 text-[12px] font-bold capitalize text-ink">{stage.kind} stage</div>
          ) : null}
          {isKnockoutStage(stage) ? (
            <KnockoutTree
              stage={stage}
              onMatchPress={onMatchPress}
              resolveHostName={resolveHostName}
              championRegistrationId={bracket.championRegistrationId}
            />
          ) : (
            <RoundList
              stage={stage}
              onMatchPress={onMatchPress}
              resolveHostName={resolveHostName}
              championRegistrationId={bracket.championRegistrationId}
            />
          )}
        </div>
      ))}
    </div>
  );
}

export default BracketView;
